// frontend/src/components/AppController.js

import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePresentationPrivacy } from '../context/PresentationPrivacyContext';
import LandingPage from './layout/LandingPage';

// Keep the landing route in the initial bundle and load every other route only
// when it is requested. This prevents public visitors from downloading the
// admin dashboards, maps, and camera/face-verification code up front.
const AdminDashboard = lazy(() => import('./admin/AdminDashboard'));
const OfficerDashboard = lazy(() => import('./officer/OfficerDashboard'));
const BecomeRider = lazy(() => import('./pages/BecomeRider'));
const BookRider = lazy(() => import('./pages/BookRider'));
const ApplyDriver = lazy(() => import('./pages/ApplyDriver'));
const ApplicationStatus = lazy(() => import('./pages/ApplicationStatus'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const NewsDetail = lazy(() => import('./pages/NewsDetail'));
const PresentationPrivacyControl = lazy(() => import('./admin/PresentationPrivacyControl'));


const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();
  const { ready: privacyReady } = usePresentationPrivacy();

  if (loading || !privacyReady) {
    return (
      <div className="loading-screen">
        <div className="spinner-large"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated || (user?.role !== 'admin' && user?.role !== 'atoda_officer')) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const OfficerRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();
  const { ready: privacyReady } = usePresentationPrivacy();

  if (loading || !privacyReady) {
    return (
      <div className="loading-screen">
        <div className="spinner-large"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== 'atoda_officer') {
    return <Navigate to="/" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  // Public content does not depend on the current account or privacy request.
  return children;
};

const AppController = () => {
  return (
    <Router>
      <Suspense
        fallback={(
          <div className="loading-screen" role="status" aria-live="polite">
            <div className="spinner-large" aria-hidden="true"></div>
            <p>Loading...</p>
          </div>
        )}
      >
        <Routes>
        {/* Allow access kung logged in or not */}
        <Route 
          path="/" 
          element={
            <PublicRoute>
              <LandingPage />
            </PublicRoute>
          } 
        />

        <Route path="/become-rider" element={<BecomeRider />} />
        <Route path="/book-rider" element={<BookRider />} />
        <Route path="/apply-driver" element={<ApplyDriver />} />
        <Route path="/check-application" element={<ApplicationStatus />} />
        <Route path="/about-us" element={<AboutPage />} />
        <Route path="/news/:id" element={<NewsDetail />} />

       
        {/* Admin only */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <RoleRouter />
            </ProtectedRoute>
          }
        />

        <Route
          path="/officer"
          element={
            <OfficerRoute>
              <OfficerDashboard />
            </OfficerRoute>
          }
        />

        <Route
          path="/control-center"
          element={<PresentationPrivacyControl />}
        />

        <Route
          path="/:privateControlPath"
          element={<PresentationPrivacyControl />}
        />

        {/* redirect sa home  */}
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Router>
  );
};

const RoleRouter = () => {
  const { user } = useAuth();
  if (user?.role === 'atoda_officer') return <OfficerDashboard />;
  return <AdminDashboard />;
};

export default AppController;
